import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import PoweredByDLSoft from "@/components/branding/PoweredByDLSoft";
import {
  LayoutDashboard,
  Users,
  Award,
  Calendar,
  ClipboardCheck,
  DollarSign,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  Building2,
  Shield,
  ShieldCheck,
  UserCog,
  MessageCircle,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useAppStore } from "@/stores/appStore";
import PendingConsentsBanner from "@/components/consent/PendingConsentsBanner";
import NotificationBell from "@/components/notifications/NotificationBell";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { SYSTEM_ROLE_LABELS, CLUB_TYPE_LABELS } from "@/types";
import { getNavGroupLabel } from "@/lib/permissions";
import type { NavItem } from "@/lib/permissions";

// Mapa de iconos por módulo
const ICONS: Record<string, typeof LayoutDashboard> = {
  LayoutDashboard,
  Building2,
  Users,
  UserCog,
  Award,
  Calendar,
  ClipboardCheck,
  DollarSign,
  BarChart3,
  Shield,
  ShieldCheck,
  Settings,
};

export default function MainLayout() {
  const { user, profile, signOut } = useAuthStore();
  const { sidebarOpen, toggleSidebar } = useAppStore();
  const { navItems, role, isClubDirector, directorOfSection } =
    usePermissions();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  // Obtener nombre para mostrar
  const displayName = profile?.displayName || user?.displayName || "Usuario";

  const roleLabel = role ? SYSTEM_ROLE_LABELS[role] : "";

  // Info del club si es director
  const directorClub = isClubDirector && profile?.directorOfClub;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-primary-50/30">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-72 bg-white/80 backdrop-blur-xl border-r border-gray-200/50 shadow-xl transform transition-all duration-300 ease-out lg:translate-x-0 flex flex-col",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-gray-200/50">
          <div className="flex items-center gap-2">
            <img
              src="/branding/insignia_icon.svg"
              alt="Insignia"
              className="w-9 h-9"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Insignia
            </span>
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Club Info (para directores de club) */}
        {directorClub && (
          <div className="p-4">
            <Link
              to={`/organization/clubs/${directorClub.id}`}
              onClick={() => {
                if (window.innerWidth < 1024) toggleSidebar();
              }}
              aria-label={`Ir a la organización de ${directorClub.name}`}
              className="block p-4 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-2"
            >
              <div className="flex items-center gap-3">
                {directorClub.logoUrl ? (
                  <img
                    src={directorClub.logoUrl}
                    alt={directorClub.name}
                    className="w-10 h-10 rounded-lg object-cover bg-white/20"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold">{directorClub.name}</p>
                  <p className="text-xs text-primary-100 mt-0.5">
                    Director de Club
                  </p>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Navigation - Filtrada por permisos y agrupada */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {(() => {
            // Agrupar items por grupo manteniendo el orden
            const groups: { key: string; items: NavItem[] }[] = [];
            let currentGroup: { key: string; items: NavItem[] } | null = null;

            navItems.forEach((item) => {
              if (!currentGroup || currentGroup.key !== item.group) {
                currentGroup = { key: item.group, items: [] };
                groups.push(currentGroup);
              }
              currentGroup.items.push(item);
            });

            return groups.map((group, groupIndex) => (
              <div key={group.key} className={cn(groupIndex > 0 && "mt-4")}>
                {groups.length > 1 && (
                  <p className="px-4 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {getNavGroupLabel(role, group.key)}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item, index) => {
                    const Icon = ICONS[item.icon] || LayoutDashboard;
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={() => {
                          if (window.innerWidth < 1024) toggleSidebar();
                        }}
                        style={{
                          animationDelay: `${(groupIndex * 3 + index) * 50}ms`,
                        }}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 animate-fade-in-up group",
                            isActive
                              ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:translate-x-1",
                          )
                        }
                      >
                        <Icon
                          className={cn(
                            "w-5 h-5 transition-transform duration-200",
                            "group-hover:scale-110",
                          )}
                        />
                        {item.name}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-gray-200/50 bg-gradient-to-t from-gray-50/80 to-transparent">
          {/* Banner compacto de consentimientos pendientes */}
          <div className="mb-3">
            <PendingConsentsBanner variant="compact" />
          </div>
          <Link
            to="/profile"
            className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300 group"
          >
            {profile?.photoUrl ? (
              <img
                src={profile.photoUrl}
                alt={displayName}
                className="w-12 h-12 rounded-xl object-cover ring-2 ring-white shadow-lg shadow-primary-500/25 group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center ring-2 ring-white shadow-lg shadow-primary-500/25 group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-bold text-base">
                  {displayName?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200">
                {displayName}
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {roleLabel}
              </p>
              {isClubDirector && directorOfSection && (
                <span
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                  title={`Solo gestionas la sección ${CLUB_TYPE_LABELS[directorOfSection]}`}
                >
                  Director de {CLUB_TYPE_LABELS[directorOfSection]}
                </span>
              )}
              {isClubDirector && !directorOfSection && directorClub && (
                <span
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                  title={`Director general de ${directorClub.name}`}
                >
                  Director general
                </span>
              )}
            </div>
          </Link>

          {/* Soporte WhatsApp (DL Soft) */}
          <a
            href={`https://wa.me/17038562045?text=${encodeURIComponent(
              `Hola, soy ${displayName} (${roleLabel || "usuario de Insignia"}). Necesito ayuda con la plataforma:`,
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 transition-all duration-200"
            title="Contactar soporte por WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Ayuda y soporte</span>
          </a>

          {/* Logout Button */}
          <button
            onClick={handleSignOut}
            className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content */}
      <div
        className={cn(
          "lg:pl-72 transition-all duration-300",
          sidebarOpen ? "" : "",
        )}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 flex items-center px-6 gap-4 shadow-sm">
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2.5 rounded-xl hover:bg-gray-100 transition-colors duration-200"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Breadcrumb or page title could go here */}
          <div className="flex-1" />

          {/* Quick actions */}
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 animate-fade-in">
          <Outlet />
        </main>

        {/* Build version */}
        <footer className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-gray-200/50">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <Link to="/terms" className="hover:text-gray-600 transition-colors">
              Términos
            </Link>
            <span className="text-gray-300">·</span>
            <Link
              to="/privacy"
              className="hover:text-gray-600 transition-colors"
            >
              Privacidad
            </Link>
            <span className="text-gray-300 hidden sm:inline">·</span>
            <span className="hidden sm:inline font-mono text-[10px] text-gray-300">
              Build: {__BUILD_TIMESTAMP__}
            </span>
          </div>
          <PoweredByDLSoft theme="light" variant="simple" size="sm" />
        </footer>
      </div>
    </div>
  );
}
